import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import GlobalFilterBar from "./GlobalFilterBar";
import KpiTile from "./KpiTile";
import ChartCard from "./ChartCard";
import AlertsBanner from "./AlertsBanner";
import InsightDataTable from "./InsightDataTable";
import DimensionExplorer from "./DimensionExplorer";
import { DASHBOARD_TABS } from "./dashboardConstants";
import {
  AXIS_TICK_STYLE,
  CHART_PALETTE,
  DashboardChartTooltip,
  GRID_STYLE,
} from "./rechartsTheme";
import { HEADER_COLOR, POPPINS, PRIMARY } from "./dashboardTypography";

const ExecutiveInsightsDashboard = ({
  activeTab,
  onTabChange,
  filters,
  filterOptions,
  onFilterChange,
  onApplyFilters,
  onResetFilters,
  dashboardData,
  loading,
  error,
  drillPath,
  onDrillDown,
  onDrillBack,
  onExport,
  dimensionExplorerConfig,
  onDimensionExplorerConfigChange,
}) => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const executive = dashboardData.executive || {};
  const social = dashboardData.social || {};
  const platform = dashboardData.platform || {};
  const sentiment = dashboardData.sentiment || {};
  const flavour = dashboardData.flavour || {};
  const topics = dashboardData.topics || {};
  const reviews = dashboardData.reviews || {};
  const alerts = dashboardData.alerts || {};
  const market = dashboardData.market || {};
  const detailed = dashboardData.detailed || {};
  const explorer = dashboardData.explorer || {};

  const renderExecutiveOverview = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <AlertsBanner summary={executive.alertsSummary} />
      </Grid>

      {(executive.kpis || []).map((kpi) => (
        <Grid item xs={12} sm={6} md={3} key={kpi.key}>
          <KpiTile
            label={kpi.label}
            value={kpi.value}
            exactValue={kpi.exactValue}
            comparison={kpi.comparison}
            loading={loading.executive}
          />
        </Grid>
      ))}

      <Grid item xs={12} md={8}>
        <ChartCard
          title="Social Media Mention Trend"
          subtitle="Daily / monthly series with drill-down"
          loading={loading.executive}
          error={error.executive}
          isEmpty={!(executive.trends || []).length}
          onExport={() => onExport("social_mentions_trend", executive.trends || [])}
        >
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={executive.trends || []}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="period" tick={AXIS_TICK_STYLE} />
              <YAxis tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="mentions" stroke="#0369a1" strokeWidth={2} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>

      <Grid item xs={12} md={4}>
        <ChartCard
          title="Sentiment Distribution"
          loading={loading.executive}
          error={error.executive}
          isEmpty={!(executive.sentimentDistribution || []).length}
          onExport={() => onExport("sentiment_distribution", executive.sentimentDistribution || [])}
        >
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={executive.sentimentDistribution || []}
                dataKey="mention_count"
                nameKey="sentiment_label"
                cx="50%"
                cy="50%"
                outerRadius={100}
                innerRadius={58}
              >
                {(executive.sentimentDistribution || []).map((entry, index) => (
                  <Cell key={`${entry.sentiment_label}-${index}`} fill={CHART_PALETTE[index % CHART_PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip content={<DashboardChartTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <ChartCard
          title="Top Flavours by Mentions"
          loading={loading.executive}
          error={error.executive}
          isEmpty={!(executive.topFlavours || []).length}
          onExport={() => onExport("top_flavours", executive.topFlavours || [])}
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={executive.topFlavours || []} layout="vertical">
              <CartesianGrid {...GRID_STYLE} />
              <XAxis type="number" tick={AXIS_TICK_STYLE} />
              <YAxis type="category" width={120} dataKey="flavour" tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Bar dataKey="total_mentions" fill="#0f766e" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <ChartCard
          title="Top Topics"
          loading={loading.executive}
          error={error.executive}
          isEmpty={!(executive.topTopics || []).length}
          onExport={() => onExport("top_topics", executive.topTopics || [])}
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={executive.topTopics || []} layout="vertical">
              <CartesianGrid {...GRID_STYLE} />
              <XAxis type="number" tick={AXIS_TICK_STYLE} />
              <YAxis type="category" width={140} dataKey="topic_keyword" tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Bar dataKey="mention_count" fill="#f97316" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>

      <Grid item xs={12}>
        <InsightDataTable
          rows={executive.platformSummary || []}
          defaultSortBy="total_engagements"
          columns={[
            { field: "platform", label: "Platform" },
            { field: "total_impressions", label: "Impressions" },
            { field: "total_engagements", label: "Engagements" },
            { field: "engagement_rate_pct", label: "Engagement Rate %" },
            { field: "total_reactions", label: "Reactions" },
            { field: "total_comments", label: "Comments" },
            { field: "total_shares", label: "Shares" },
            { field: "total_video_views", label: "Video Views" },
            { field: "total_saves", label: "Saves" },
            { field: "total_link_clicks", label: "Link Clicks" },
          ]}
        />
      </Grid>
    </Grid>
  );

  const renderSocial = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <ChartCard title="Impressions Trend by Platform" loading={loading.social} error={error.social} isEmpty={!(social.trend || []).length}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={social.trend || []}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="period" tick={AXIS_TICK_STYLE} />
              <YAxis tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Line type="monotone" dataKey="impressions" stroke="#0369a1" connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartCard title="Engagement Trend by Platform" loading={loading.social} error={error.social} isEmpty={!(social.trend || []).length}>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={social.trend || []}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="period" tick={AXIS_TICK_STYLE} />
              <YAxis tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Area type="monotone" dataKey="engagements" stroke="#0f766e" fill="#99f6e4" connectNulls={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
    </Grid>
  );

  const renderPlatform = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <ChartCard title="Platform Engagement Comparison" loading={loading.platform} error={error.platform} isEmpty={!(platform.rows || []).length}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={platform.rows || []} layout="vertical" onClick={(point) => onDrillDown("platform", point?.activeLabel)}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis type="number" tick={AXIS_TICK_STYLE} />
              <YAxis dataKey="platform" type="category" width={110} tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Bar dataKey="total_engagements" fill="#0f766e" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartCard title="Platform Efficiency" loading={loading.platform} error={error.platform} isEmpty={!(platform.rows || []).length}>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="engagement_rate_pct" name="Engagement Rate" tick={AXIS_TICK_STYLE} />
              <YAxis dataKey="total_impressions" name="Impressions" tick={AXIS_TICK_STYLE} />
              <ZAxis dataKey="total_engagements" range={[80, 500]} name="Engagements" />
              <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<DashboardChartTooltip />} />
              <Scatter data={platform.rows || []} fill="#0369a1" />
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12}>
        <InsightDataTable
          rows={platform.rows || []}
          defaultSortBy="total_engagements"
          columns={[
            { field: "platform", label: "Platform" },
            { field: "total_impressions", label: "Impressions" },
            { field: "total_engagements", label: "Engagements" },
            { field: "engagement_rate_pct", label: "Engagement Rate %" },
            { field: "total_reactions", label: "Reactions" },
            { field: "total_comments", label: "Comments" },
            { field: "total_shares", label: "Shares" },
            { field: "total_video_views", label: "Video Views" },
            { field: "total_followers_gained", label: "Followers Gained" },
            { field: "total_net_follower_growth", label: "Net Follower Growth" },
            { field: "total_saves", label: "Saves" },
            { field: "total_link_clicks", label: "Link Clicks" },
            { field: "story_replies", label: "Story Replies" },
            { field: "active_days", label: "Active Days" },
            { field: "save_rate_pct", label: "Save Rate %" },
            { field: "video_view_rate_pct", label: "Video View Rate %" },
            { field: "share_rate_pct", label: "Share Rate %" },
            { field: "avg_daily_engagements", label: "Avg Daily Engagements" },
          ]}
        />
      </Grid>
    </Grid>
  );

  const renderSentiment = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <ChartCard title="Sentiment Distribution" loading={loading.sentiment} error={error.sentiment} isEmpty={!(sentiment.distribution || []).length}>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={sentiment.distribution || []} dataKey="mention_count" nameKey="sentiment_label" innerRadius={56} outerRadius={100}>
                {(sentiment.distribution || []).map((entry, index) => (
                  <Cell key={`${entry.sentiment_label}-${index}`} fill={CHART_PALETTE[index % CHART_PALETTE.length]} />
                ))}
              </Pie>
              <Legend />
              <Tooltip content={<DashboardChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={7}>
        <ChartCard title="Net Sentiment Trend" loading={loading.sentiment} error={error.sentiment} isEmpty={!(sentiment.monthly || []).length}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={sentiment.monthly || []}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="analysis_month" tick={AXIS_TICK_STYLE} />
              <YAxis tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Line dataKey="net_sentiment" stroke="#be123c" connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12}>
        <InsightDataTable
          rows={sentiment.monthly || []}
          defaultSortBy="analysis_month"
          columns={[
            { field: "analysis_month", label: "Month" },
            { field: "positive_mentions", label: "Positive Mentions" },
            { field: "positive_pct", label: "Positive %" },
            { field: "neutral_mentions", label: "Neutral Mentions" },
            { field: "neutral_pct", label: "Neutral %" },
            { field: "negative_mentions", label: "Negative Mentions" },
            { field: "negative_pct", label: "Negative %" },
            { field: "net_sentiment", label: "Net Sentiment" },
            { field: "positive_to_negative_ratio", label: "Positive:Negative Ratio" },
          ]}
        />
      </Grid>
    </Grid>
  );

  const renderFlavour = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <ChartCard title="Top Flavours by Mentions" loading={loading.flavour} error={error.flavour} isEmpty={!(flavour.rows || []).length}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={flavour.rows || []} layout="vertical" onClick={(point) => onDrillDown("flavour", point?.activeLabel)}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis type="number" tick={AXIS_TICK_STYLE} />
              <YAxis type="category" width={120} dataKey="flavour" tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Bar dataKey="total_mentions" fill="#0f766e" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartCard title="Flavour Growth vs Popularity" loading={loading.flavour} error={error.flavour} isEmpty={!(flavour.rows || []).length}>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="total_mentions" name="Total Mentions" tick={AXIS_TICK_STYLE} />
              <YAxis dataKey="market_growth_pct" name="Market Growth %" tick={AXIS_TICK_STYLE} />
              <ZAxis dataKey="total_engagements" range={[80, 500]} name="Engagements" />
              <Tooltip content={<DashboardChartTooltip />} />
              <Scatter data={flavour.rows || []} fill="#f97316" />
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12}>
        <InsightDataTable
          rows={flavour.rows || []}
          defaultSortBy="total_mentions"
          columns={[
            { field: "flavour", label: "Flavour" },
            { field: "total_mentions", label: "Total Mentions" },
            { field: "social_mentions", label: "Social Mentions" },
            { field: "review_mentions", label: "Review Mentions" },
            { field: "total_engagements", label: "Total Engagements" },
            { field: "avg_sentiment_score", label: "Avg Sentiment Score" },
            { field: "positive_pct", label: "Positive %" },
            { field: "market_rank", label: "Market Rank" },
            { field: "market_growth_pct", label: "Market Growth %" },
          ]}
        />
      </Grid>
    </Grid>
  );

  const renderTopics = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <ChartCard title="Top Topics" loading={loading.topics} error={error.topics} isEmpty={!(topics.rows || []).length}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topics.rows || []} layout="vertical" onClick={(point) => onDrillDown("topic", point?.activeLabel)}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis type="number" tick={AXIS_TICK_STYLE} />
              <YAxis type="category" width={150} dataKey="topic_keyword" tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Bar dataKey="mention_count" fill="#0284c7" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartCard title="Topic Opportunity Matrix" loading={loading.topics} error={error.topics} isEmpty={!(topics.rows || []).length}>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="mention_count" name="Mentions" tick={AXIS_TICK_STYLE} />
              <YAxis dataKey="yoy_growth_pct" name="YoY Growth %" tick={AXIS_TICK_STYLE} />
              <ZAxis dataKey="weight" range={[80, 500]} name="Weight" />
              <Tooltip content={<DashboardChartTooltip />} />
              <Scatter data={topics.rows || []} fill="#7c3aed" />
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12}>
        <InsightDataTable
          rows={topics.rows || []}
          defaultSortBy="mention_count"
          columns={[
            { field: "topic_keyword", label: "Topic / Keyword" },
            { field: "mention_count", label: "Mentions" },
            { field: "weight", label: "Weight" },
            { field: "yoy_growth_pct", label: "YoY Growth %" },
            { field: "brand", label: "Brand" },
            { field: "source", label: "Source" },
            { field: "period", label: "Period" },
          ]}
        />
      </Grid>
    </Grid>
  );

  const renderReviews = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <ChartCard title="Review Volume" loading={loading.reviews} error={error.reviews} isEmpty={!(reviews.rows || []).length}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={reviews.rows || []}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="dimension_value" tick={AXIS_TICK_STYLE} />
              <YAxis tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Bar dataKey="review_count" fill="#0369a1" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartCard title="Review Opportunity Matrix" loading={loading.reviews} error={error.reviews} isEmpty={!(reviews.rows || []).length}>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="review_count" name="Review Count" tick={AXIS_TICK_STYLE} />
              <YAxis dataKey="avg_sentiment_score" name="Sentiment Score" tick={AXIS_TICK_STYLE} />
              <ZAxis dataKey="negative_count" range={[80, 500]} name="Negative Count" />
              <Tooltip content={<DashboardChartTooltip />} />
              <Scatter data={reviews.rows || []} fill="#be123c" />
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12}>
        <InsightDataTable
          rows={reviews.rows || []}
          defaultSortBy="review_count"
          columns={[
            { field: "dimension_type", label: "Dimension Type" },
            { field: "dimension_value", label: "Dimension Value" },
            { field: "review_count", label: "Review Count" },
            { field: "avg_sentiment_score", label: "Avg Sentiment Score" },
            { field: "positive_count", label: "Positive Count" },
            { field: "negative_count", label: "Negative Count" },
            { field: "neutral_count", label: "Neutral Count" },
            { field: "positive_pct", label: "Positive %" },
            { field: "negative_pct", label: "Negative %" },
          ]}
        />
      </Grid>
    </Grid>
  );

  const renderAlerts = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <ChartCard title="Severity Distribution" loading={loading.alerts} error={error.alerts} isEmpty={!(alerts.severityDistribution || []).length}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={alerts.severityDistribution || []} dataKey="count" nameKey="severity" outerRadius={108} innerRadius={55}>
                {(alerts.severityDistribution || []).map((entry, index) => (
                  <Cell key={`${entry.severity}-${index}`} fill={CHART_PALETTE[index % CHART_PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip content={<DashboardChartTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={8}>
        <ChartCard title="YoY Change by Topic" loading={loading.alerts} error={error.alerts} isEmpty={!(alerts.rows || []).length}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={alerts.rows || []}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="topic_keyword" tick={AXIS_TICK_STYLE} />
              <YAxis tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Bar dataKey="yoy_change_pct" fill="#f97316" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12}>
        <InsightDataTable
          rows={alerts.rows || []}
          defaultSortBy="alert_date"
          columns={[
            { field: "alert_date", label: "Alert Date" },
            { field: "alert_type", label: "Alert Type" },
            { field: "topic_keyword", label: "Topic / Keyword" },
            { field: "current_period_value", label: "Current Period" },
            { field: "previous_period_value", label: "Previous Period" },
            { field: "yoy_change_pct", label: "YoY Change %" },
            { field: "severity", label: "Severity" },
            { field: "description", label: "Description" },
            { field: "recommended_action", label: "Recommended Action" },
            { field: "brand", label: "Brand" },
            { field: "source", label: "Source" },
          ]}
        />
      </Grid>
    </Grid>
  );

  const renderMarket = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <ChartCard title="Market Trend" loading={loading.market} error={error.market} isEmpty={!(market.rows || []).length}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={market.rows || []}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="year" tick={AXIS_TICK_STYLE} />
              <YAxis tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Legend />
              <Line dataKey="metric_value" stroke="#0f766e" connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={5}>
        <ChartCard title="Market Comparison" loading={loading.market} error={error.market} isEmpty={!(market.rows || []).length}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={market.rows || []} layout="vertical">
              <CartesianGrid {...GRID_STYLE} />
              <XAxis type="number" tick={AXIS_TICK_STYLE} />
              <YAxis type="category" dataKey="category" width={100} tick={AXIS_TICK_STYLE} />
              <Tooltip content={<DashboardChartTooltip />} />
              <Bar dataKey="metric_value" fill="#0ea5e9" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Grid>
      <Grid item xs={12}>
        <InsightDataTable
          rows={market.rows || []}
          defaultSortBy="year"
          columns={[
            { field: "category", label: "Category" },
            { field: "metric_name", label: "Metric" },
            { field: "metric_value", label: "Value" },
            { field: "unit", label: "Unit" },
            { field: "year", label: "Year" },
            { field: "source_detail", label: "Source Detail" },
            { field: "source", label: "Source" },
          ]}
        />
      </Grid>
    </Grid>
  );

  const renderDetailed = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <DimensionExplorer
          config={dimensionExplorerConfig}
          data={explorer.rows || []}
          onConfigChange={onDimensionExplorerConfigChange}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
        <InsightDataTable
          rows={detailed.rows || []}
          defaultSortBy="total_impressions"
          columns={[
            { field: "brand", label: "Brand" },
            { field: "period", label: "Period" },
            { field: "platform", label: "Platform" },
            { field: "source", label: "Source" },
            { field: "total_impressions", label: "Impressions" },
            { field: "total_engagements", label: "Engagements" },
            { field: "total_reactions", label: "Reactions" },
            { field: "total_comments", label: "Comments" },
            { field: "total_shares", label: "Shares" },
            { field: "total_video_views", label: "Video Views" },
            { field: "total_followers_gained", label: "Followers Gained" },
            { field: "total_net_follower_growth", label: "Net Follower Growth" },
            { field: "total_saves", label: "Saves" },
            { field: "total_link_clicks", label: "Link Clicks" },
            { field: "engagement_rate_pct", label: "Engagement Rate %" },
            { field: "save_rate_pct", label: "Save Rate %" },
            { field: "video_view_rate_pct", label: "Video View Rate %" },
            { field: "share_rate_pct", label: "Share Rate %" },
            { field: "avg_daily_engagements", label: "Avg Daily Engagements" },
          ]}
        />
      </Grid>
    </Grid>
  );

  const sectionRenderer = {
    0: renderExecutiveOverview,
    1: renderSocial,
    2: renderPlatform,
    3: renderSentiment,
    4: renderFlavour,
    5: renderTopics,
    6: renderReviews,
    7: renderAlerts,
    8: renderMarket,
    9: renderDetailed,
  };

  return (
    <Box sx={{ height: "100%", overflow: "auto", background: "linear-gradient(180deg, #f8fcff 0%, #f8fafc 38%, #ffffff 100%)" }}>
      <GlobalFilterBar
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={onFilterChange}
        onApply={onApplyFilters}
        onReset={onResetFilters}
        isLoading={Object.values(loading).some(Boolean)}
      />

      <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: 2.5 }}>
        <Stack spacing={1.5} sx={{ mb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="h5" sx={{ ...POPPINS, fontWeight: 700, letterSpacing: -0.2, color: HEADER_COLOR }}>
              Executive Social Media & Brand Insights Dashboard
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TuneIcon fontSize="small" />}
              onClick={() => setIsFilterDrawerOpen(true)}
              sx={{ ...POPPINS, fontSize: "0.8rem", fontWeight: 600, textTransform: "none", borderColor: PRIMARY, color: PRIMARY }}
            >
              Filters
            </Button>
          </Stack>
          {drillPath.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="caption" color="text.secondary" sx={{ ...POPPINS, fontSize: "0.72rem" }}>Drill-down:</Typography>
              {drillPath.map((crumb, index) => (
                <Chip
                  key={`${crumb.dimension}-${index}`}
                  size="small"
                  label={`${crumb.dimension}: ${crumb.value}`}
                  onDelete={index === drillPath.length - 1 ? onDrillBack : undefined}
                  sx={{ ...POPPINS, fontSize: "0.72rem", height: 24 }}
                />
              ))}
            </Stack>
          )}
        </Stack>

        <Tabs
          value={activeTab}
          onChange={(_, value) => onTabChange(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          TabIndicatorProps={{ style: { backgroundColor: PRIMARY } }}
          sx={{
            mb: 2,
            "& .MuiTab-root": {
              ...POPPINS,
              fontSize: "0.8rem",
              textTransform: "none",
              fontWeight: 500,
              minHeight: 40,
              color: "#6B7280",
            },
            "& .Mui-selected": {
              color: `${HEADER_COLOR} !important`,
              fontWeight: 600,
            },
          }}
        >
          {DASHBOARD_TABS.map((tab) => (
            <Tab key={tab} label={tab} />
          ))}
        </Tabs>

        {sectionRenderer[activeTab] ? sectionRenderer[activeTab]() : renderExecutiveOverview()}
      </Box>
    </Box>
  );
};

ExecutiveInsightsDashboard.propTypes = {
  activeTab: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  filterOptions: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  dashboardData: PropTypes.object,
  loading: PropTypes.object,
  error: PropTypes.object,
  drillPath: PropTypes.arrayOf(
    PropTypes.shape({
      dimension: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  onDrillDown: PropTypes.func.isRequired,
  onDrillBack: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  dimensionExplorerConfig: PropTypes.shape({
    dimension: PropTypes.string,
    metric: PropTypes.string,
    chartType: PropTypes.string,
  }).isRequired,
  onDimensionExplorerConfigChange: PropTypes.func.isRequired,
};

ExecutiveInsightsDashboard.defaultProps = {
  filterOptions: {},
  dashboardData: {},
  loading: {},
  error: {},
  drillPath: [],
};

export default ExecutiveInsightsDashboard;
